/* Daily discovery only. Product markup and availability remain Shopify-rendered. */
(() => {
  'use strict';
  if (customElements.get('vmax-daily-cards')) return;

  const dateFormat = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit'
  });
  function localDay() {
    const parts = Object.fromEntries(dateFormat.formatToParts(new Date()).map(p => [p.type, p.value]));
    return Math.floor(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / 86400000);
  }
  function hash(text) {
    let value = 2166136261;
    for (const character of text) value = Math.imul(value ^ character.charCodeAt(0), 16777619);
    return value >>> 0;
  }
  function gcd(a, b) {
    while (b) [a, b] = [b, a % b];
    return a;
  }
  function dailyPage(day, pages, collection) {
    let stride = hash(collection) % pages || 1;
    while (gcd(stride, pages) !== 1) stride++;
    return (day * stride + hash(collection)) % pages + 1;
  }
  function choose(pool, day, count, collection) {
    // A stable shuffled deck, advanced daily, avoids repeats on adjacent days.
    const unique = new Map();
    for (const node of pool) if (node.dataset.productId && node.dataset.inStock === 'true') unique.set(node.dataset.productId, node);
    const deck = Array.from(unique.values()).sort((a, b) => {
      const difference = hash(collection + a.dataset.productId) - hash(collection + b.dataset.productId);
      return difference || a.dataset.productId.localeCompare(b.dataset.productId);
    });
    if (!deck.length) return [];
    const take = Math.min(count, deck.length);
    const step = Math.max(1, Math.min(take, deck.length - take));
    const start = day * step % deck.length;
    return Array.from({length: take}, (_, i) => deck[(start + i) % deck.length]);
  }

  customElements.define('vmax-daily-cards', class extends HTMLElement {
    connectedCallback() {
      if (this.refresh) return;
      this.grid = this.querySelector('.vmax-home-discovery__grid');
      const template = this.querySelector('template[data-vmax-daily-pool]');
      if (!this.grid || !template) return;
      this.pool = Array.from(template.content.children);
      this.snapshotAt = Number(this.dataset.stockCheckedAt) * 1000 || this.snapshotAt || Date.now();
      this.refresh = () => this.update();
      this.focusOut = () => queueMicrotask(() => this.refresh?.());
      this.addEventListener('focusout', this.focusOut);
      document.addEventListener('visibilitychange', this.refresh);
      window.addEventListener('pageshow', this.refresh);
      this.update();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.timeout);
      this.controller?.abort();
      document.removeEventListener('visibilitychange', this.refresh);
      window.removeEventListener('pageshow', this.refresh);
      this.removeEventListener('focusout', this.focusOut);
      this.pending = null;
      this.refresh = null;
      this.day = null;
      this.loading = false;
    }

    render(pool, day, restoreFocus = false) {
      const count = Math.min(12, Math.max(1, Number(this.dataset.count) || 10));
      const selected = choose(pool, day, count, this.dataset.collection || '');
      const focused = restoreFocus && this.contains(document.activeElement);
      const focusedId = focused ? document.activeElement.closest('[data-product-id]')?.dataset.productId : null;
      // Empty is intentional: never retain cards whose positive stock expired.
      this.grid.replaceChildren(...selected.map(node => node.cloneNode(true)));
      const empty = this.querySelector('[data-vmax-stock-empty]');
      if (empty) empty.hidden = selected.length > 0;
      if (focused) {
        const target = Array.from(this.grid.children).find(node => node.dataset.productId === focusedId)?.querySelector('a') ||
          this.closest('[data-vmax-home-discovery]')?.querySelector('.vmax-home-discovery__products-header a');
        target?.focus({preventScroll: true});
      }
    }

    async update() {
      clearTimeout(this.timer);
      if (!this.isConnected) return;
      this.timer = setTimeout(this.refresh, 60000);
      if (document.hidden) return;
      const day = localDay();
      const now = Date.now();
      if (this.pending?.day === day && !this.contains(document.activeElement)) {
        if (now - this.pending.checkedAt < 300000) {
          this.render(this.pending.pool, this.pending.selectionDay);
          this.dataset.state = 'ready';
        }
        this.pending = null;
      }
      if (this.loading || (this.day === day && now - this.checkedAt < 300000)) return;
      const initial = this.day == null && now - this.snapshotAt < 300000 && now >= this.snapshotAt;
      const revalidate = this.day != null;
      this.pending = null;
      this.day = day;
      this.checkedAt = initial ? this.snapshotAt : now;
      this.controller?.abort();
      clearTimeout(this.timeout);
      if (initial) this.render(this.pool, day);
      else if (!revalidate) this.render([], day);
      this.dataset.state = initial ? 'local' : 'checking';
      const pages = Math.min(500, Math.max(1, Number(this.dataset.pages) || 1));
      const firstPage = dailyPage(day, pages, this.dataset.collection || '');
      const selectionDay = Math.floor(day / pages);
      const count = Math.min(12, Math.max(1, Number(this.dataset.count) || 10));
      const controller = new AbortController();
      this.controller = controller;
      this.loading = true;
      this.timeout = setTimeout(() => controller.abort(), 8000);
      const pool = new Map();
      let poolCheckedAt = now;
      try {
        // Sparse pages are expected after filtering. Bound both requests and time.
        for (let offset = 0; offset < Math.min(pages, 8); offset++) {
          const page = (firstPage - 1 + offset) % pages + 1;
          let nodes;
          if (initial && page === Number(this.dataset.page)) {
            nodes = this.pool;
            poolCheckedAt = Math.min(poolCheckedAt, this.snapshotAt);
          } else {
            const url = new URL(window.location.href);
            url.hash = '';
            url.searchParams.delete('sections');
            url.searchParams.set('section_id', this.dataset.sectionId);
            url.searchParams.set(this.dataset.pageParam || 'page', String(page));
            url.searchParams.set('vmax_day', String(day));
            url.searchParams.set('vmax_stock_check', String(Math.floor(now / 300000)));
            const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin', cache: 'no-store'});
            if (!response.ok) throw new Error('Section unavailable');
            const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
            const component = Array.from(documentResult.querySelectorAll('vmax-daily-cards')).find(
              item => item.dataset.sectionId === this.dataset.sectionId &&
                item.dataset.collection === this.dataset.collection && Number(item.dataset.page) === page
            );
            const template = component?.querySelector('template[data-vmax-daily-pool]');
            if (!template) throw new Error('Invalid section');
            nodes = Array.from(template.content.children);
          }
          if (!this.isConnected || this.controller !== controller || this.day !== day) return;
          if (controller.signal.aborted) throw new Error('Expired stock response');
          for (const node of nodes) {
            if (node.dataset.productId && node.dataset.inStock === 'true') pool.set(node.dataset.productId, node);
          }
          if (pool.size >= count) break;
        }
        this.checkedAt = poolCheckedAt;
        const eligible = Array.from(pool.values());
        if (!revalidate && this.contains(document.activeElement)) {
          this.pending = {pool: eligible, day, selectionDay, checkedAt: poolCheckedAt};
          this.dataset.state = 'pending';
        } else {
          this.render(eligible, selectionDay, revalidate);
          this.dataset.state = 'ready';
        }
      } catch (error) {
        if (this.isConnected && this.controller === controller) {
          if (!initial) this.render(Array.from(pool.values()), selectionDay, true);
          this.dataset.state = 'fallback';
        }
      } finally {
        if (this.controller === controller) {
          clearTimeout(this.timeout);
          this.loading = false;
        }
      }
    }
  });
})();
